/**
 * ResizeObserver 错误处理器
 * 解决 Element Plus 表格组件在某些情况下触发的 ResizeObserver 循环错误
 */

let isHandlerInstalled = false
let originalConsoleError = null
let originalWindowError = null

/**
 * 检查是否为 ResizeObserver 错误
 */
function isResizeObserverError(error) {
  const message = error?.message || error?.toString?.() || ''
  return message.includes('ResizeObserver loop completed with undelivered notifications')
}

/**
 * 错误事件处理器
 */
function handleError(event) {
  if (isResizeObserverError(event.error) || isResizeObserverError(event.message)) {
    // 静默处理 ResizeObserver 错误
    if (event.preventDefault) event.preventDefault()
    if (event.stopPropagation) event.stopPropagation()
    if (event.stopImmediatePropagation) event.stopImmediatePropagation()

    // 标记错误已处理
    if (event.error) event.error.handled = true
    event.handled = true

    return true // 阻止默认处理
  }
  return false
}

/**
 * 未处理的 Promise 拒绝处理器
 */
function handleUnhandledRejection(event) {
  if (isResizeObserverError(event.reason)) {
    event.preventDefault()
    return false
  }
}

/**
 * 安装全局 ResizeObserver 错误处理器
 */
export function installResizeObserverHandler() {
  if (isHandlerInstalled) {
    return
  }

  // 1. 处理全局错误事件
  window.addEventListener('error', handleError, { capture: true, passive: false })
  window.addEventListener('unhandledrejection', handleUnhandledRejection, { capture: true, passive: false })

  // 2. 过滤控制台错误输出
  originalConsoleError = console.error
  console.error = (...args) => {
    const message = args.join(' ')
    if (!isResizeObserverError({ message })) {
      originalConsoleError.apply(console, args)
    }
  }

  // 3. 拦截 window.onerror
  originalWindowError = window.onerror
  window.onerror = (message, source, lineno, colno, error) => {
    if (isResizeObserverError({ message })) {
      return true // 阻止默认行为
    }
    if (originalWindowError) {
      return originalWindowError.call(window, message, source, lineno, colno, error)
    }
    return false
  }

  // 4. 开发环境下移除 webpack 错误覆盖层
  if (process.env.NODE_ENV === 'development') {
    // 拦截 webpack 错误处理模块
    const originalErrorHandler = window.onerror
    const originalErrorEventHandler = window.addEventListener

    // 重写 addEventListener 来拦截 webpack overlay 的错误监听
    window.addEventListener = function(type, listener, options) {
      if (type === 'error' && typeof listener === 'function') {
        const wrappedListener = function(event) {
          // 检查是否为 ResizeObserver 错误
          if (isResizeObserverError(event.error) || isResizeObserverError(event.message)) {
            event.preventDefault()
            event.stopImmediatePropagation()
            return false
          }
          return listener.apply(this, arguments)
        }
        return originalErrorEventHandler.call(this, type, wrappedListener, options)
      }
      return originalErrorEventHandler.call(this, type, listener, options)
    }
    const removeWebpackOverlay = () => {
      const overlays = [
        document.querySelector('webpack-dev-server-client-overlay'),
        document.querySelector('[data-webpack-overlay]'),
        document.querySelector('#webpack-dev-server-client-overlay')
      ].filter(Boolean)

      overlays.forEach(overlay => {
        if (overlay && overlay.shadowRoot) {
          const errorContent = overlay.shadowRoot.textContent || ''
          if (isResizeObserverError({ message: errorContent })) {
            overlay.style.display = 'none'
            overlay.remove()
          }
        }
      })
    }

    // 监控 DOM 变化，自动移除相关的错误覆盖层
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 &&
              (node.tagName === 'WEBPACK-DEV-SERVER-CLIENT-OVERLAY' ||
               node.getAttribute?.('data-webpack-overlay') ||
               node.id === 'webpack-dev-server-client-overlay')) {
            // 检查是否为 ResizeObserver 相关错误
            setTimeout(() => {
              const content = node.shadowRoot?.textContent || node.textContent || ''
              if (isResizeObserverError({ message: content })) {
                node.style.display = 'none'
                node.remove()
              }
            }, 0)
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    // 定时清理
    const cleanupInterval = setInterval(removeWebpackOverlay, 500)

    // 页面卸载时清理
    window.addEventListener('beforeunload', () => {
      clearInterval(cleanupInterval)
      observer.disconnect()
    })
  }

  isHandlerInstalled = true
  console.log('🛡️ ResizeObserver 错误处理器已安装')
}

/**
 * 卸载错误处理器（主要用于测试或特殊情况）
 */
export function uninstallResizeObserverHandler() {
  if (!isHandlerInstalled) {
    return
  }

  window.removeEventListener('error', handleError, { capture: true })
  window.removeEventListener('unhandledrejection', handleUnhandledRejection, { capture: true })

  // 恢复原始的 console.error
  if (originalConsoleError) {
    console.error = originalConsoleError
    originalConsoleError = null
  }

  // 恢复原始的 window.onerror
  if (originalWindowError !== undefined) {
    window.onerror = originalWindowError
    originalWindowError = null
  }

  isHandlerInstalled = false
  console.log('🛡️ ResizeObserver 错误处理器已卸载')
}
